import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { get } from 'lodash';
import { createPatient, getPatient, updatePatient } from './_api';
import { IPatientFormValues } from './_types';
import PatientFormComponent from './PatientFormComponent';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import useAlerts from 'components/Alerts/useAlerts';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useWithEntityTitle } from 'utils/hooks/useWithEntityTitle';

const BIRTH_NUMBER_SYSTEM = 'http://hl7.cz/fhir/NamingSystem/cz-rcis';
const INSURANCE_SYSTEM = 'https://ncez.mzcr.cz/fhir/sid/cpoj';
const IDENTIFIER_TYPE_BIRTH_NUMBER = 'birthNumber';
const IDENTIFIER_TYPE_INSURANCE = 'insurance';
const IDENTIFIER_TYPE_OTHER = 'other';

const PatientForm: React.FC = () => {
  const { t } = useTranslation('Patients');
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toggleLoader } = useAppGlobals();
  const { addSuccessAlert, addErrorAlert } = useAlerts();
  const { compactMode } = useAppInfo();

  const linkBack = searchParams.get('back') || '/patients';

  const isCreating = !id || id === 'create';
  const [entity, setEntity] = useState<any>(null);
  const [isFetching, setIsFetching] = useState<boolean>(true);

  const [defaultValues, setDefaultValues] = useState<IPatientFormValues>({
    active: true,
    firstName: '',
    lastName: '',
    birthNumber: '',
    birthDate: null,
    gender: '',
    deceased: false,
    deceasedDate: null,
    identifier: [],
    telecom: [],
    address: [],
  });

  const { title } = useWithEntityTitle(isCreating, entity, t, 'createTitle', 'editTitle');

  const loadEntity = async () => {
    if (isCreating) {
      setIsFetching(false);
      return;
    }

    toggleLoader(true);
    try {
      const patientData = await getPatient(parseInt(id!));
      if (patientData) {
        setEntity(patientData);

        const names = get(patientData, 'name', []);
        const identifiers = get(patientData, 'identifier', []);
        const birthNumberIdentifier = identifiers.find(
          (identifier: any) => get(identifier, 'system', '') === BIRTH_NUMBER_SYSTEM,
        );
        const birthNumber = get(birthNumberIdentifier, 'value', '');
        const otherIdentifiers = identifiers
          .filter((identifier: any) => get(identifier, 'system', '') !== BIRTH_NUMBER_SYSTEM)
          .map((identifier: any) => {
            const system = get(identifier, 'system', '');
            if (system === INSURANCE_SYSTEM) {
              return {
                ...identifier,
                uiSystemType: IDENTIFIER_TYPE_INSURANCE,
                oid: '',
              };
            }
            return {
              ...identifier,
              uiSystemType: IDENTIFIER_TYPE_OTHER,
              oid: system,
            };
          });
        const firstName = names.length > 0 ? (names[0].given ? names[0].given.join(' ') : '') : '';
        const lastName = names.length > 0 ? names[0].family || '' : '';

        const defaultValues: IPatientFormValues = {
          id: patientData.id,
          active: get(patientData, 'active', true),
          firstName,
          lastName,
          birthNumber,
          birthDate: patientData.birthDate ? new Date(patientData.birthDate) : null,
          gender: get(patientData, 'gender', ''),
          deceased: get(patientData, 'deceasedBoolean', false),
          deceasedDate: patientData.deceasedDateTime
            ? new Date(patientData.deceasedDateTime)
            : null,
          identifier: otherIdentifiers,
          telecom: get(patientData, 'telecom', []),
          address: get(patientData, 'address', []),
        };
        setDefaultValues(defaultValues);
      }
    } catch (error) {
      addErrorAlert(t('loadError'));
      console.error('Error loading patient:', error);
    } finally {
      setIsFetching(false);
      toggleLoader(false);
    }
  };

  const buildPayload = (data: IPatientFormValues) => {
    const birthNumberIdentifier = {
      system: BIRTH_NUMBER_SYSTEM,
      value: (data.birthNumber || '').trim(),
    };
    const otherIdentifiers = (data.identifier || [])
      .filter((identifier: any) => get(identifier, 'value', '').trim() !== '')
      .map((identifier: any) => {
        const uiSystemType = get(identifier, 'uiSystemType', IDENTIFIER_TYPE_OTHER);
        const system =
          uiSystemType === IDENTIFIER_TYPE_INSURANCE
            ? INSURANCE_SYSTEM
            : uiSystemType === IDENTIFIER_TYPE_BIRTH_NUMBER
              ? BIRTH_NUMBER_SYSTEM
              : get(identifier, 'oid', '').trim();

        return {
          ...identifier,
          system,
          oid: undefined,
          uiSystemType: undefined,
        };
      })
      .filter((identifier: any) => get(identifier, 'system', '').trim() !== '');

    return {
      active: data.active,
      name: [
        {
          family: data.lastName,
          given: [data.firstName],
        },
      ],
      gender: data.gender || undefined,
      birthDate: data.birthDate ? data.birthDate.toISOString().split('T')[0] : undefined,
      deceasedBoolean: data.deceased,
      deceasedDateTime: data.deceasedDate ? data.deceasedDate.toISOString() : undefined,
      identifier: [birthNumberIdentifier, ...otherIdentifiers],
      telecom: (data.telecom || []).map((t: any) => ({ ...t, use: 'home' })),
      address: (data.address || []).map((a: any) => ({ ...a, use: 'home', type: 'both' })),
    };
  };

  const handleSubmit = async (data: IPatientFormValues) => {
    toggleLoader(true);
    try {
      const payload = buildPayload(data);
      if (isCreating) {
        await createPatient(payload);
        addSuccessAlert(t('createSuccess'));
      } else {
        await updatePatient({ ...payload, id: parseInt(id!) });
        addSuccessAlert(t('updateSuccess'));
      }
      const finalLinkBack = linkBack.includes('?')
        ? `${linkBack}&reload=true`
        : `${linkBack}?reload=true`;
      navigate(finalLinkBack);
    } catch (error: any) {
      if (isCreating && error?.response?.status === 409) {
        addErrorAlert(t('createConflictError'));
      } else {
        addErrorAlert(isCreating ? t('createError') : t('updateError'));
      }
      console.error('Error saving patient:', error);
    } finally {
      toggleLoader(false);
    }
  };

  const handleSubmitAndOpen = async (data: IPatientFormValues) => {
    toggleLoader(true);
    try {
      const payload = buildPayload(data);
      const created = await createPatient(payload);
      addSuccessAlert(t('createSuccess'));
      navigate(`/patients/${created?.id}`);
    } catch (error: any) {
      if (error?.response?.status === 409) {
        addErrorAlert(t('createConflictError'));
      } else {
        addErrorAlert(t('createError'));
      }
      console.error('Error saving patient:', error);
    } finally {
      toggleLoader(false);
    }
  };

  useEffect(() => {
    loadEntity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (isFetching) return null;

  return (
    <PatientFormComponent
      isCreating={isCreating}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onSaveAndOpen={isCreating ? handleSubmitAndOpen : undefined}
      linkBack={linkBack}
      compactMode={compactMode}
      title={title}
    />
  );
};

export default PatientForm;
